import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Switch,
  Checkbox,
  Chip,
  Tooltip,
  CircularProgress,
  FormControlLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RemoveIcon from "@mui/icons-material/Remove";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import useIsMobile from "../../../Core/hook/useIsMobile";
import { useNotifier } from "../../../Core/Notifier";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  ACTIONS,
  ACTION_KEYS,
  ActionKey,
  MatrixGroup,
  MatrixFlag,
  MatrixModule,
} from "./permissionUtils";

export const categoryIcon = (cat: string) => {
  const c = (cat || "").toLowerCase();
  const sx = { fontSize: 20, color: "#475569" };
  if (c.includes("dashboard")) return <DashboardOutlinedIcon sx={sx} />;
  if (c.includes("student")) return <SchoolOutlinedIcon sx={sx} />;
  if (c.includes("academic")) return <MenuBookOutlinedIcon sx={sx} />;
  if (c.includes("aba") || c.includes("therapy"))
    return <PsychologyOutlinedIcon sx={sx} />;
  if (c.includes("staff") || c.includes("hr")) return <BadgeOutlinedIcon sx={sx} />;
  if (c.includes("finance")) return <PaymentsOutlinedIcon sx={sx} />;
  if (c.includes("commun")) return <ChatBubbleOutlineIcon sx={sx} />;
  if (c.includes("report")) return <AssessmentOutlinedIcon sx={sx} />;
  if (c.includes("admin")) return <SettingsOutlinedIcon sx={sx} />;
  return <FolderOutlinedIcon sx={sx} />;
};

interface Props {
  groups: MatrixGroup[];
  flags: MatrixFlag[];
  assigned: Set<number>;
  onChange?: (next: Set<number>) => void; // omitted => read-only
  loading?: boolean;
  // Batch 23 — view/edit mode differences:
  //   "view": granted-only by default with a Show-all toggle; categories
  //           with zero granted collapse into a summary row.
  //   "edit": full matrix always; categories with granted access are
  //           expanded on mount, empty ones stay collapsed.
  mode?: "view" | "edit";
}

const PermissionMatrix: React.FC<Props> = ({
  groups,
  flags,
  assigned,
  onChange,
  loading,
  mode = onChange ? "edit" : "view",
}) => {
  const isMobile = useIsMobile();
  const readOnly = !onChange;
  const { showMessage } = useNotifier();
  const [search, setSearch] = useState("");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [collapsedSeeded, setCollapsedSeeded] = useState(false);
  const [showAllInView, setShowAllInView] = useState(false);
  const [emptyCategoriesShown, setEmptyCategoriesShown] = useState(false);

  const has = (id: number | null) => !!id && assigned.has(id);

  // Flags grouped by category so we can render them inline in each
  // category card ("Additional actions") instead of a standalone
  // "Special Permissions" section that has no CRUD alignment. Any
  // flag whose category has no CRUD module falls into a fallback
  // section rendered at the bottom.
  const flagsByCategory = useMemo(() => {
    const map: Record<string, MatrixFlag[]> = {};
    flags.forEach((f) => {
      const k = f.category || "Other";
      (map[k] = map[k] || []).push(f);
    });
    return map;
  }, [flags]);
  const categoriesWithModules = useMemo(
    () => new Set(groups.map((g) => g.category)),
    [groups]
  );
  const orphanFlags = useMemo(
    () => flags.filter((f) => !categoriesWithModules.has(f.category || "")),
    [flags, categoriesWithModules]
  );

  // Reverse map for the Create→View helper (frontend nudge only).
  const moduleByActionId = useMemo(() => {
    const byId = new Map<number, MatrixModule>();
    groups.forEach((g) =>
      g.modules.forEach((m) => {
        ACTION_KEYS.forEach((a) => {
          const id = m.actions[a];
          if (id) byId.set(id, m);
        });
      })
    );
    return byId;
  }, [groups]);

  const withViewImplied = (candidate: Set<number>): Set<number> => {
    const next = new Set(candidate);
    groups.forEach((g) =>
      g.modules.forEach((m) => {
        const vId = m.actions.view;
        if (!vId) return;
        const nonViewOn = (["create", "edit", "delete"] as ActionKey[]).some(
          (a) => {
            const id = m.actions[a];
            return id && next.has(id);
          }
        );
        if (nonViewOn) next.add(vId);
      })
    );
    return next;
  };

  const toggle = (id: number | null) => {
    if (!id || !onChange) return;
    const module = moduleByActionId.get(id);
    const isView = module?.actions.view === id;
    const turningOn = !assigned.has(id);
    const next = new Set(assigned);

    if (turningOn) {
      next.add(id);
      if (module && !isView && module.actions.view) {
        next.add(module.actions.view);
      }
    } else {
      next.delete(id);
      if (isView && module) {
        const remaining = (["create", "edit", "delete"] as ActionKey[]).filter(
          (a) => {
            const aid = module.actions[a];
            return aid && next.has(aid);
          }
        );
        if (remaining.length > 0) {
          showMessage(
            `Cleared View on "${module.display_name}" while ${remaining.join(
              ", "
            )} remain. The create/edit screens need View to populate pickers — those flows may break.`
          );
        }
      }
    }
    onChange(next);
  };

  const moduleIds = (m: MatrixModule) =>
    ACTION_KEYS.map((a) => m.actions[a]).filter((x): x is number => !!x);

  const toggleModule = (m: MatrixModule) => {
    if (!onChange) return;
    const ids = moduleIds(m);
    const allOn = ids.length > 0 && ids.every((id) => assigned.has(id));
    const next = new Set(assigned);
    ids.forEach((id) => (allOn ? next.delete(id) : next.add(id)));
    onChange(withViewImplied(next));
  };

  const columnIds = useMemo(() => {
    const map: Record<ActionKey, number[]> = {
      view: [], create: [], edit: [], delete: [],
    };
    groups.forEach((g) =>
      g.modules.forEach((m) =>
        ACTION_KEYS.forEach((a) => {
          const id = m.actions[a];
          if (id) map[a].push(id);
        })
      )
    );
    return map;
  }, [groups]);

  const toggleGlobalColumn = (a: ActionKey) => {
    if (!onChange) return;
    const ids = columnIds[a] || [];
    const allOn = ids.length > 0 && ids.every((id) => assigned.has(id));
    const next = new Set(assigned);
    ids.forEach((id) => (allOn ? next.delete(id) : next.add(id)));
    onChange(withViewImplied(next));
  };

  const toggleCategoryColumn = (modules: MatrixModule[], a: ActionKey) => {
    if (!onChange) return;
    const ids = modules
      .map((m) => m.actions[a])
      .filter((x): x is number => !!x);
    if (ids.length === 0) return;
    const allOn = ids.every((id) => assigned.has(id));
    const next = new Set(assigned);
    ids.forEach((id) => (allOn ? next.delete(id) : next.add(id)));
    onChange(withViewImplied(next));
  };

  // --- filter + counts ---------------------------------------------------
  const term = search.trim().toLowerCase();
  const matchModule = (m: MatrixModule) =>
    !term ||
    m.display_name.toLowerCase().includes(term) ||
    m.prefix.toLowerCase().includes(term);
  const matchFlag = (f: MatrixFlag) =>
    !term ||
    f.label.toLowerCase().includes(term) ||
    f.name.toLowerCase().includes(term);

  const filteredGroups = useMemo(
    () =>
      groups
        .map((g) => ({ ...g, modules: g.modules.filter(matchModule) }))
        .filter(
          (g) =>
            g.modules.length > 0 ||
            (flagsByCategory[g.category] || []).some(matchFlag)
        ),
    [groups, term, flagsByCategory]
  );
  const filteredOrphanFlags = useMemo(
    () => orphanFlags.filter(matchFlag),
    [orphanFlags, term]
  );

  const categoryGrantedCount = (modules: MatrixModule[], catFlags: MatrixFlag[] = []) => {
    let n = 0;
    modules.forEach((m) =>
      ACTION_KEYS.forEach((a) => {
        const id = m.actions[a];
        if (id && assigned.has(id)) n += 1;
      })
    );
    catFlags.forEach((f) => { if (assigned.has(f.id)) n += 1; });
    return n;
  };
  const categoryTotalCount = (modules: MatrixModule[], catFlags: MatrixFlag[] = []) => {
    let n = catFlags.length;
    modules.forEach((m) =>
      ACTION_KEYS.forEach((a) => { if (m.actions[a]) n += 1; })
    );
    return n;
  };
  const categoryHasWrite = (modules: MatrixModule[], catFlags: MatrixFlag[] = []) => {
    const anyWriteAction = modules.some((m) =>
      (["create", "edit", "delete"] as ActionKey[]).some(
        (a) => m.actions[a] && assigned.has(m.actions[a] as number)
      )
    );
    // Flags are single-action; if any granted it's a write-shape capability.
    const anyFlag = catFlags.some((f) => assigned.has(f.id));
    return anyWriteAction || anyFlag;
  };

  // --- initial collapse state -------------------------------------------
  // Seed once when groups first arrive. Edit mode: expand only categories
  // with at least one granted permission. View mode: same (granted-only
  // rendering means empty categories are already hidden by default).
  useEffect(() => {
    if (collapsedSeeded || groups.length === 0) return;
    const next: Record<string, boolean> = {};
    groups.forEach((g) => {
      const catFlags = flagsByCategory[g.category] || [];
      const granted = categoryGrantedCount(g.modules, catFlags);
      next[g.category] = granted === 0;
    });
    setCollapsed(next);
    setCollapsedSeeded(true);
    // Intentionally excluding `assigned` — we only seed once, on first
    // arrival. Later toggles from the user are their choice, not ours.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, collapsedSeeded]);

  // Search: auto-expand categories that contain a match. When search
  // clears, restore the seeded collapse state.
  useEffect(() => {
    if (!term) return;
    setCollapsed((prev) => {
      const next = { ...prev };
      filteredGroups.forEach((g) => { next[g.category] = false; });
      return next;
    });
  }, [term, filteredGroups]);

  const expandAll = () =>
    setCollapsed(Object.fromEntries(groups.map((g) => [g.category, false])));
  const collapseAll = () =>
    setCollapsed(Object.fromEntries(groups.map((g) => [g.category, true])));

  // --- renderers ---------------------------------------------------------

  const ActionControl = ({ id }: { id: number | null }) => {
    if (!id) return <RemoveIcon sx={{ color: "#cbd5e1", fontSize: 18 }} />;
    if (readOnly)
      return has(id) ? (
        <CheckCircleIcon sx={{ color: "#16a34a", fontSize: 20 }} />
      ) : (
        <RemoveIcon sx={{ color: "#cbd5e1", fontSize: 18 }} />
      );
    return (
      <Switch size="small" checked={has(id)} onChange={() => toggle(id)} />
    );
  };

  const ModuleRow = ({ m }: { m: MatrixModule }) => {
    const ids = moduleIds(m);
    const allOn = ids.length > 0 && ids.every((id) => assigned.has(id));
    const someOn = ids.some((id) => assigned.has(id));
    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "minmax(180px,1fr) repeat(4, 96px)",
          alignItems: "center", gap: 1, px: 2, py: 1,
          borderTop: "1px solid #f1f5f9",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          {!readOnly && (
            <Checkbox size="small" checked={allOn} indeterminate={!allOn && someOn}
              onChange={() => toggleModule(m)} sx={{ p: 0.25 }} />
          )}
          <Typography sx={{ fontWeight: 600, fontSize: 14, color: "#334155" }}>
            {m.display_name}
          </Typography>
        </Stack>

        {isMobile ? (
          <Stack direction="row" flexWrap="wrap" sx={{ mt: 0.5 }} rowGap={0.5} columnGap={2}>
            {ACTIONS.map((a) => (
              <Stack key={a.key} direction="row" alignItems="center" spacing={0.5}>
                <Typography sx={{ fontSize: 12, color: a.color, minWidth: 44 }}>
                  {a.label}
                </Typography>
                <ActionControl id={m.actions[a.key]} />
              </Stack>
            ))}
          </Stack>
        ) : (
          ACTIONS.map((a) => (
            <Box key={a.key} sx={{ textAlign: "center" }}>
              <ActionControl id={m.actions[a.key]} />
            </Box>
          ))
        )}
      </Box>
    );
  };

  const FlagRow = ({ f }: { f: MatrixFlag }) => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "minmax(180px,1fr) repeat(4, 96px)",
        alignItems: "center", gap: 1, px: 2, py: 1,
        borderTop: "1px solid #f1f5f9",
        background: "#fafbff",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography sx={{ fontWeight: 600, fontSize: 14, color: "#334155" }}>
          {f.label}
        </Typography>
        <Chip size="small" label="action" sx={{ height: 18, fontSize: 10,
          background: "#eef2ff", color: "#3730a3" }} />
      </Stack>
      {isMobile ? (
        <Box sx={{ mt: 0.5 }}>
          <ActionControl id={f.id} />
        </Box>
      ) : (
        <>
          <Box />
          <Box />
          <Box />
          <Box sx={{ textAlign: "center" }}>
            <ActionControl id={f.id} />
          </Box>
        </>
      )}
    </Box>
  );

  const CategorySection = ({
    category, modules, catFlags,
  }: {
    category: string;
    modules: MatrixModule[];
    catFlags: MatrixFlag[];
  }) => {
    const isCollapsed = !!collapsed[category];
    const granted = categoryGrantedCount(modules, catFlags);
    const total = categoryTotalCount(modules, catFlags);

    // View mode + granted-only: hide modules/flags with nothing granted.
    const showAll = mode === "edit" || showAllInView;
    const visibleModules = showAll
      ? modules
      : modules.filter((m) => moduleIds(m).some((id) => assigned.has(id)));
    const visibleFlags = showAll
      ? catFlags
      : catFlags.filter((f) => assigned.has(f.id));

    return (
      <Box
        sx={{
          mb: 2,
          borderRadius: 2,
          border: "1px solid #e2e8f0",
          // NB: no `overflow: hidden` here — that would break the sticky
          // column header below (position:sticky needs a scrollable
          // ancestor without clipping). Border-radius is preserved via
          // the header/body's own rounded corners.
          background: "#fff",
        }}
      >
        <Stack
          direction="row" alignItems="center" spacing={1}
          sx={{
            px: 2, py: 1.25,
            background: "#f8fafc", cursor: "pointer",
            borderRadius: "8px 8px 0 0",
          }}
          onClick={() => setCollapsed((p) => ({ ...p, [category]: !p[category] }))}
        >
          {categoryIcon(category)}
          <Typography sx={{ fontWeight: 700, color: "#0f172a", flex: 1 }}>
            {category}
          </Typography>
          <Chip size="small" label={`${granted}/${total} granted`}
            sx={{
              background: granted > 0 ? "#ecfdf5" : "#f1f5f9",
              color: granted > 0 ? "#047857" : "#64748b",
              fontWeight: 600,
            }} />
          <Chip size="small" label={`${modules.length + catFlags.length} modules`} />
          <IconButton size="small">
            {isCollapsed ? <UnfoldMoreIcon /> : <UnfoldLessIcon />}
          </IconButton>
        </Stack>
        {!isCollapsed && !isMobile && (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "minmax(180px,1fr) repeat(4, 96px)",
                alignItems: "center", gap: 1,
                px: 2, py: 0.75,
                background: "#fff",
                borderTop: "1px solid #f1f5f9",
                position: "sticky", top: 0, zIndex: 2,
              }}
            >
              <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>MODULE</Typography>
              {ACTIONS.map((a) => {
                const ids = visibleModules
                  .map((m) => m.actions[a.key])
                  .filter((x): x is number => !!x);
                const allOn = ids.length > 0 && ids.every((id) => assigned.has(id));
                return (
                  <Box key={a.key} sx={{ textAlign: "center" }}>
                    <Stack spacing={0.25} alignItems="center">
                      <Stack direction="row" spacing={0.25} alignItems="center" justifyContent="center">
                        <Typography sx={{ fontSize: 12, color: a.color, fontWeight: 700 }}>
                          {a.label.toUpperCase()}
                        </Typography>
                        {a.key === "view" && (
                          <Tooltip title="Create, Edit and Delete screens rely on View to populate pickers and lists. Ticking any of those auto-ticks View. Clearing View while another action remains will warn — the flows may break.">
                            <InfoOutlinedIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
                          </Tooltip>
                        )}
                      </Stack>
                      {!readOnly && ids.length > 0 && (
                        <Tooltip title={`${allOn ? "Clear" : "Grant"} all ${a.label} in ${category}`}>
                          <Checkbox size="small" checked={allOn}
                            onChange={() => toggleCategoryColumn(visibleModules, a.key)}
                            sx={{ p: 0.25 }} />
                        </Tooltip>
                      )}
                    </Stack>
                  </Box>
                );
              })}
            </Box>
            {visibleModules.length === 0 && visibleFlags.length === 0 && (
              <Typography sx={{ px: 2, py: 2, color: "#94a3b8", fontSize: 13 }}>
                No granted permissions in this category. Use "Show all
                permissions" above to view every option.
              </Typography>
            )}
            {visibleModules.map((m) => <ModuleRow key={m.prefix} m={m} />)}
            {visibleFlags.length > 0 && (
              <>
                <Box sx={{
                  px: 2, py: 0.5, borderTop: "1px solid #e2e8f0",
                  background: "#f8fafc",
                }}>
                  <Typography sx={{ fontSize: 11, color: "#64748b",
                    fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase" }}>
                    Additional actions
                  </Typography>
                </Box>
                {visibleFlags.map((f) => <FlagRow key={f.id} f={f} />)}
              </>
            )}
          </>
        )}
        {!isCollapsed && isMobile && (
          <>
            {visibleModules.map((m) => <ModuleRow key={m.prefix} m={m} />)}
            {visibleFlags.map((f) => <FlagRow key={f.id} f={f} />)}
          </>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Collapse-summary line for view mode: N categories with no access.
  const emptyCategoriesInView =
    mode === "view" && !showAllInView
      ? filteredGroups.filter(
          (g) =>
            categoryGrantedCount(g.modules, flagsByCategory[g.category] || []) === 0
        )
      : [];

  const visibleCategoryGroups =
    mode === "view" && !showAllInView
      ? filteredGroups.filter(
          (g) =>
            categoryGrantedCount(g.modules, flagsByCategory[g.category] || []) > 0
        )
      : filteredGroups;

  return (
    <>
      {/* Toolbar */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ sm: "center" }}
        sx={{ mb: 2 }}
      >
        <TextField
          size="small" placeholder="Search permissions…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          sx={{ background: "#fff", minWidth: 260 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#94a3b8" }} />
              </InputAdornment>
            ),
          }}
        />
        {mode === "view" && (
          <FormControlLabel
            control={
              <Switch size="small"
                checked={showAllInView}
                onChange={(_, v) => setShowAllInView(v)} />
            }
            label={<Typography sx={{ fontSize: 13, color: "#334155" }}>Show all permissions</Typography>}
            sx={{ ml: 1 }}
          />
        )}
        <Box sx={{ flex: 1 }} />
        <Chip
          label={`${assigned.size} permissions ${readOnly ? "granted" : "selected"}`}
          sx={{ background: "#eef2ff", color: "#3730a3", fontWeight: 600 }}
        />
        {!isMobile && (
          <>
            <Button size="small" startIcon={<UnfoldMoreIcon />} onClick={expandAll}>
              Expand
            </Button>
            <Button size="small" startIcon={<UnfoldLessIcon />} onClick={collapseAll}>
              Collapse
            </Button>
          </>
        )}
      </Stack>

      {/* Global column quick-toggles (edit only) */}
      {!isMobile && !readOnly && (
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} alignItems="center">
          <Typography sx={{ fontSize: 12, color: "#64748b" }}>
            Toggle every module (all categories):
          </Typography>
          {ACTIONS.map((a) => {
            const ids = columnIds[a.key] || [];
            const allOn = ids.length > 0 && ids.every((id) => assigned.has(id));
            return (
              <Tooltip key={a.key} title={`Toggle every ${a.label}`}>
                <Chip
                  size="small" label={a.label}
                  onClick={() => toggleGlobalColumn(a.key)}
                  variant={allOn ? "filled" : "outlined"}
                  sx={{
                    cursor: "pointer",
                    color: allOn ? "#fff" : a.color,
                    background: allOn ? a.color : "transparent",
                    borderColor: a.color,
                  }}
                />
              </Tooltip>
            );
          })}
        </Stack>
      )}

      {visibleCategoryGroups.map((g) => (
        <CategorySection
          key={g.category}
          category={g.category}
          modules={g.modules}
          catFlags={(flagsByCategory[g.category] || []).filter(matchFlag)}
        />
      ))}

      {emptyCategoriesInView.length > 0 && (
        <Box sx={{
          mb: 2, p: 1.5, borderRadius: 2,
          border: "1px dashed #cbd5e1", background: "#f8fafc",
        }}>
          <Stack direction="row" alignItems="center" spacing={1}
            sx={{ cursor: "pointer" }}
            onClick={() => setEmptyCategoriesShown((v) => !v)}>
            <Typography sx={{ fontSize: 13, color: "#64748b", flex: 1 }}>
              {emptyCategoriesShown ? "Hide" : "Show"}
              {" "}
              {emptyCategoriesInView.length}
              {" "}
              categor{emptyCategoriesInView.length === 1 ? "y" : "ies"} with no access
            </Typography>
            <IconButton size="small">
              {emptyCategoriesShown ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
            </IconButton>
          </Stack>
          {emptyCategoriesShown && (
            <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1 }}>
              {emptyCategoriesInView.map((g) => (
                <Chip key={g.category} size="small" variant="outlined"
                  label={g.category}
                  sx={{ borderColor: "#e2e8f0", color: "#64748b", background: "#fff" }} />
              ))}
            </Stack>
          )}
        </Box>
      )}

      {filteredOrphanFlags.length > 0 && (
        <Box sx={{
          mb: 2, borderRadius: 2, border: "1px solid #e2e8f0",
          background: "#fff",
        }}>
          <Stack direction="row" alignItems="center" spacing={1}
            sx={{ px: 2, py: 1.25, background: "#f8fafc" }}>
            {categoryIcon("admin")}
            <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
              Other actions
            </Typography>
          </Stack>
          {filteredOrphanFlags.map((f) => <FlagRow key={f.id} f={f} />)}
        </Box>
      )}

      {visibleCategoryGroups.length === 0
        && emptyCategoriesInView.length === 0
        && filteredOrphanFlags.length === 0 && (
        <Typography sx={{ textAlign: "center", color: "#94a3b8", py: 4 }}>
          {term
            ? `No permissions match "${search}".`
            : mode === "view"
              ? "This role has no permissions granted. Turn on \"Show all permissions\" to see the full matrix."
              : "No permissions to display."}
        </Typography>
      )}
    </>
  );
};

export default PermissionMatrix;
